import { useState, type MouseEvent } from 'react'
import { Menu } from '@mui/material'
import { useTranslation } from 'react-i18next'
import {
  type OrganisationAddressDto,
  useCreateOrganisationAddress,
  useDeleteOrganisationAddress,
  useGetOrganisationAddresses,
  useUpdateOrganisationAddress,
  useUpdateOrganisationAddressConfirmationStatus,
} from '@dhl/mdp-api'
import { type GridColDef, Pagination } from '@dhl/components'
import { ConfirmationDialog } from '@dhl/confirmation-dialog'
import { DeleteIcon, EditSolidIcon, MoreVerticalIcon, PlusIcon } from '@dhl-ui/icons'
import { Alert, Body1, Box, Button, Card, Grid, IconButton, MenuItem } from '@dhl-ui/components'
import { AdminDataGrid, useCurrentUser, useNotificationMessage } from '~/common'
import { COUNTRIES } from '~/common/config/country'
import { getErrorMessage } from '~/common/util/axiosUtil'
import type { Organisation } from '~/modules/organisations/model'
import { OrganisationAddressForm, type OrganisationAddressFormValues } from './OrganisationAddressForm'

type Props = {
  organisation: Organisation
}

type AddressRow = OrganisationAddressDto & {
  rowId: string
}

type ConfirmationAction = 'delete' | 'confirm' | 'unconfirm'

export const OrganisationAddressesCard = ({ organisation }: Props) => {
  const { t } = useTranslation()
  const { hasRole } = useCurrentUser()
  const canManageAddresses = hasRole('ORGANISATION_ADDRESSES')
  const { showError, showSuccess } = useNotificationMessage()
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(10)
  const [addressFormOpen, setAddressFormOpen] = useState(false)
  const [selectedAddress, setSelectedAddress] = useState<OrganisationAddressDto>()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [confirmationAction, setConfirmationAction] = useState<ConfirmationAction>()
  const menuOpen = Boolean(anchorEl)
  const { id: organisationId } = organisation

  const { data: addresses = [], error, isError, isFetching, refetch } = useGetOrganisationAddresses(organisationId)
  const { mutateAsync: createAddress } = useCreateOrganisationAddress()
  const { mutateAsync: updateAddress } = useUpdateOrganisationAddress()
  const { mutateAsync: deleteAddress } = useDeleteOrganisationAddress()
  const { mutateAsync: updateConfirmationStatus } = useUpdateOrganisationAddressConfirmationStatus()

  const rows: AddressRow[] = addresses.map((address, index) => ({
    ...address,
    rowId: address.id ?? `address-${index}`,
  }))

  const closeAddressForm = () => {
    setAddressFormOpen(false)
    setSelectedAddress(undefined)
  }

  const openNewAddressForm = () => {
    setSelectedAddress(undefined)
    setAddressFormOpen(true)
  }

  const openEditAddressForm = () => {
    setAddressFormOpen(true)
    setAnchorEl(null)
  }

  const onAddressSubmit = async (values: OrganisationAddressFormValues) => {
    const addressDto: OrganisationAddressDto = {
      countryCode: values.countryCode,
      postalCode: values.postalCode.trim(),
      city: values.city.trim(),
      confirmed: values.confirmed,
      ...(values.street.trim() ? { street: values.street.trim() } : {}),
      ...(values.number.trim() ? { number: values.number.trim() } : {}),
      ...(values.addition.trim() ? { addition: values.addition.trim() } : {}),
      ...(values.id ? { id: values.id } : {}),
    }

    try {
      const addressId = selectedAddress?.id
      if (selectedAddress && !addressId) {
        showError(t('admin.organisationsPage.addresses.requestError'))
        return
      }
      if (selectedAddress && addressId) {
        await updateAddress({ id: organisationId, addressId, data: addressDto })
      } else {
        await createAddress({
          id: organisationId,
          data: { ...addressDto, confirmed: false },
        })
      }
      showSuccess(t(`admin.organisationsPage.addresses.${selectedAddress ? 'editSuccess' : 'addSuccess'}`))
      await refetch()
      closeAddressForm()
    } catch (error) {
      showError(getErrorMessage(error, t('admin.organisationsPage.addresses.errorMessage')))
    }
  }

  const openConfirmation = (action: ConfirmationAction) => {
    setAnchorEl(null)
    setConfirmationAction(action)
  }

  const closeConfirmation = () => {
    setConfirmationAction(undefined)
  }

  const confirmAction = async () => {
    if (!selectedAddress?.id) {
      showError(t('admin.organisationsPage.addresses.requestError'))
      return
    }

    try {
      if (confirmationAction === 'delete') {
        await deleteAddress({ id: organisationId, addressId: selectedAddress.id })
      } else if (confirmationAction === 'confirm' || confirmationAction === 'unconfirm') {
        await updateConfirmationStatus({
          id: organisationId,
          addressId: selectedAddress.id,
          data: { confirmed: confirmationAction === 'confirm' },
        })
      } else {
        return
      }
      showSuccess(t(`admin.organisationsPage.addresses.${confirmationAction}Success`))
      closeConfirmation()
      setSelectedAddress(undefined)
      await refetch()
    } catch (error) {
      showError(getErrorMessage(error, t('admin.organisationsPage.addresses.requestError')))
    }
  }

  const handleRowAction = (event: MouseEvent<HTMLElement>, address: OrganisationAddressDto) => {
    setSelectedAddress(address)
    setAnchorEl(event.currentTarget)
  }

  const columns: GridColDef<AddressRow>[] = [
    {
      field: 'address',
      headerName: t('admin.organisationsPage.addresses.address'),
      flex: 3,
      sortable: false,
      renderCell: ({ row }) => {
        const country = COUNTRIES.find(({ value }) => value === row.countryCode)?.label ?? row.countryCode
        const streetLine = [row.street, row.number, row.addition].filter(Boolean).join(' ')
        return (
          <Body1>
            {[streetLine, [row.postalCode, row.city].filter(Boolean).join(' '), country].filter(Boolean).join(', ')}
          </Body1>
        )
      },
    },
    {
      field: 'confirmed',
      headerName: t('admin.organisationsPage.addresses.status'),
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Grid container gap={1} alignItems="center" wrap="nowrap">
          <Box
            aria-hidden="true"
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: row.confirmed ? 'success.main' : 'warning.main',
              flexShrink: 0,
            }}
          />
          <Body1>{t(`admin.organisationsPage.addresses.${row.confirmed ? 'confirmed' : 'unconfirmed'}`)}</Body1>
        </Grid>
      ),
    },
    ...(canManageAddresses
      ? [
          {
            field: 'actions',
            headerName: t('admin.organisationsPage.addresses.actions'),
            width: 70,
            sortable: false,
            headerAlign: 'center' as const,
            align: 'center' as const,
            renderCell: ({ row }: { row: AddressRow }) => (
              <IconButton
                aria-label={t('admin.organisationsPage.addresses.actions')}
                aria-controls={menuOpen ? 'organisation-address-menu' : undefined}
                aria-haspopup="true"
                onClick={(event) => handleRowAction(event, row)}
              >
                <MoreVerticalIcon />
              </IconButton>
            ),
          },
        ]
      : []),
  ]

  return (
    <>
      <Card>
        <Card.Header
          title={t('admin.organisationsPage.addresses.title')}
          endNode={
            canManageAddresses ? (
              <Button
                size="small"
                color="primary"
                variant="contained"
                startIcon={<PlusIcon />}
                onClick={openNewAddressForm}
              >
                {t('admin.organisationsPage.addresses.add')}
              </Button>
            ) : undefined
          }
        />
        <Card.Content>
          {isError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {getErrorMessage(error, t('admin.organisationsPage.addresses.loadError'))}
            </Alert>
          )}
          <AdminDataGrid<AddressRow>
            data-testid="organisation-addresses-table"
            getRowId={(row) => row.rowId}
            loading={isFetching}
            rows={rows}
            columns={columns}
            autoHeight
            paginationModel={{ page, pageSize }}
            rowHeight={56}
            columnHeaderHeight={48}
            disableRowSelectionOnClick
            disableColumnFilter
            hideFooter
          />
          <Box px={2} py={1}>
            <Pagination
              page={page}
              count={rows.length}
              onPageChange={(_, nextPage) => setPage(nextPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={setPageSize}
            />
          </Box>
        </Card.Content>
      </Card>

      {canManageAddresses && (
        <Menu id="organisation-address-menu" anchorEl={anchorEl} open={menuOpen} onClose={() => setAnchorEl(null)}>
          <MenuItem onClick={openEditAddressForm} icon={<EditSolidIcon />}>
            {t('admin.organisationsPage.addresses.edit')}
          </MenuItem>
          {selectedAddress && (
            <MenuItem onClick={() => openConfirmation(selectedAddress.confirmed ? 'unconfirm' : 'confirm')}>
              {t(`admin.organisationsPage.addresses.${selectedAddress.confirmed ? 'unconfirm' : 'confirm'}`)}
            </MenuItem>
          )}
          <MenuItem onClick={() => openConfirmation('delete')} icon={<DeleteIcon />} isDestructive>
            {t('admin.organisationsPage.addresses.delete')}
          </MenuItem>
        </Menu>
      )}

      {addressFormOpen && canManageAddresses && (
        <OrganisationAddressForm
          address={selectedAddress}
          defaultCountryCode={organisation.data.address?.countryCode ?? 'NL'}
          onSubmit={onAddressSubmit}
          onClose={closeAddressForm}
        />
      )}

      {confirmationAction && (
        <ConfirmationDialog
          open
          titleText={t(`admin.organisationsPage.addresses.${confirmationAction}Title`)}
          acceptText={t(`admin.organisationsPage.addresses.${confirmationAction}`)}
          rejectText={t('admin.accountNumbers.cancel')}
          onAccept={confirmAction}
          onReject={closeConfirmation}
          onClose={closeConfirmation}
        >
          <Body1>
            {t(`admin.organisationsPage.addresses.${confirmationAction}Message`, {
              address: selectedAddress
                ? [selectedAddress.street, selectedAddress.number, selectedAddress.addition].filter(Boolean).join(' ')
                : '',
            })}
          </Body1>
          {confirmationAction !== 'delete' && <Body1>{t('admin.organisationsPage.addresses.validationWarning')}</Body1>}
        </ConfirmationDialog>
      )}
    </>
  )
}
